import axios from 'axios';
import cookie from 'js-cookie';
import cookieHelper from 'cookie';

//Types
import { SET_USER_AUTH_DETAIL_STATE, SET_USER_AUTH_STATE, SET_USER_INITIAL_AUTH_DETAIL_STATE } from './../user/types';
import { RESET_REDUX_STORE_TO_INITIAL_STATE } from './../user/types';
// Redux
import { getAffiliateDetails, setSuperUserState } from '../user/actions';
import { getServicesErrorObj } from '../servicesDowntime/actions'
// Utils
import { getBasicAuthToken } from '../../../core/utils/BasicAuthTokenGetter';
import { getOrganizationCode } from '../../../core/utils/OrganizationValueGetter';
import { persistLogout } from '../../store';
import TokenManager from '../../../core/utils/TokenManager/TokenManager';
// Constants
import { SSO_CDH_COMPANY_KEY } from '../../../core/utils/Constants/Constants';

const USER_AUTH_URL_V1: string = "/v1/affiliate-portal/affiliate/login";
const USER_AUTH_URL_V2: string = "/v2/affiliate-portal/affiliate/login";
const REFRESH_URL: string = '/v1/affiliate-portal/refresh';
const VALIDATE_TOKEN_URL: string = '/v1/affiliate-portal/affiliate/validateToken';

const AFFILIATE_NUMBER_KEY: string = "affiliate-number";
const AFFILIATE_ID_KEY: string = "affiliate-id";
const AFFILIATE_EMAIL_ID: string = "affiliate-email";
const AUTHENTICATED_BY_OCTA: string = "authenticated_by_octa";
const LOGGED_OUT: string = 'logged-out';
const SUPER_USER_KEY: string = "super-user";

let BASIC_AUTH_TOKEN: string;

const REFRESH_TOKEN_EXPIRY: number = 29 / 1440;
const COOKIE_PATH = '/';


const USER_AUTH_DEFAULTS_V1: any = {
  grant_type: 'password',
  scope: 'affiliate_portal'
};

const USER_AUTH_DEFAULTS_V2: any = {
  type: 'loginAuth'
};

export const setUserAuthState = (payload: any) => ({
  type: SET_USER_AUTH_STATE,
  payload
});
export const setUserAuthDetail = (payload: any) => ({
  type: SET_USER_AUTH_DETAIL_STATE,
  payload
});

export const reSetUserAuth = () => ({
  type: SET_USER_INITIAL_AUTH_DETAIL_STATE
});

export const authenticate = (
  credentials: any,
  defaults: any = USER_AUTH_DEFAULTS_V1,
  endpoint: string = USER_AUTH_URL_V1,
  isRefresh: boolean = false
) => (dispatch: Function, getState: Function) =>
    new Promise((resolve, reject) => {
      const state = getState();
      const loginViaApBackendFlag: boolean = state.user.launchDarklyFlags?.loginViaApBackend ?? false;

      const isV2Login: boolean = loginViaApBackendFlag && !isRefresh;

      const referringCompany: string = cookieHelper.parse(document.cookie).cdhCompany;

      // Check authenticated_by_octa dynamically from session storage
      const isAuthByOcta = Boolean(sessionStorage.getItem(AUTHENTICATED_BY_OCTA));

      BASIC_AUTH_TOKEN = isRefresh && isAuthByOcta ? getBasicAuthToken(referringCompany, true) : getBasicAuthToken(referringCompany);

      const requestPayload = isV2Login ? {
        ...USER_AUTH_DEFAULTS_V2,
        organisation: credentials.orgCode,
        email: credentials.username,
        password: credentials.password
      } : {
        ...defaults,
        ...credentials
      };

      const requestEndpoint = isV2Login ? USER_AUTH_URL_V2 : endpoint;

      axios(requestEndpoint, {
        method: "POST",
        data: requestPayload,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${BASIC_AUTH_TOKEN}`,
        },
      })
        .then((response) => {
          if (response.data) {
            const {
              access_token,
              refresh_token,
              affiliate_number,
              affiliate_id,
            } = response.data;

            TokenManager.setAccessToken(access_token);
            TokenManager.setRefreshToken(refresh_token, REFRESH_TOKEN_EXPIRY);

            affiliate_id && cookie.set(AFFILIATE_ID_KEY, affiliate_id, {
              path: COOKIE_PATH
            });

            affiliate_number && affiliate_number.length > 0 && cookie.set(AFFILIATE_NUMBER_KEY, affiliate_number, {
              path: COOKIE_PATH
            });

            affiliate_number && affiliate_number.length > 0 && sessionStorage.setItem(AFFILIATE_NUMBER_KEY, affiliate_number);
            affiliate_id && affiliate_id.length > 0 && sessionStorage.setItem(AFFILIATE_ID_KEY, affiliate_id);
            credentials?.username && sessionStorage.setItem(AFFILIATE_EMAIL_ID, credentials.username);

            const bannerStatusLocalObject: any = JSON.parse(localStorage.getItem('bannerStatus') || '{}')

            if (!('ssoBanner' in bannerStatusLocalObject))
              localStorage.setItem('bannerStatus', JSON.stringify({
                ...bannerStatusLocalObject,
                ssoBanner: true
              }));

            dispatch(setUserAuthDetail(response.data));
          }

          if (isRefresh) {
            resolve(response);
          } else {
            dispatch(getAffiliateDetails())
              .then(() => resolve(response))
              .catch(reject);
          }
        })
        .catch(reject);
    });

export const validateToken = (token: string, email: string, affiliateNumber: string, accountSfId: string) => (dispatch: Function) => {
  return new Promise((resolve, reject) => {
    const referringCompany: string = cookieHelper.parse(document.cookie).cdhCompany;
    BASIC_AUTH_TOKEN = getBasicAuthToken(referringCompany);

    const requestPayload = {
      token: token,
      organisation: getOrganizationCode(referringCompany)
    };

    axios(VALIDATE_TOKEN_URL, {
      method: "POST",
      data: requestPayload,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Basic ${BASIC_AUTH_TOKEN}`,
      },
    })
      .then((response) => {
        if (response.data) {
          const {
            access_token,
            refresh_token,
          } = response.data;

          TokenManager.setAccessToken(access_token);
          TokenManager.setRefreshToken(refresh_token, REFRESH_TOKEN_EXPIRY);

          accountSfId && cookie.set(AFFILIATE_ID_KEY, accountSfId, {
            path: COOKIE_PATH
          });

          affiliateNumber && affiliateNumber.length > 0 && cookie.set(AFFILIATE_NUMBER_KEY, affiliateNumber, {
            path: COOKIE_PATH
          });

          affiliateNumber && affiliateNumber.length > 0 && sessionStorage.setItem(AFFILIATE_NUMBER_KEY, affiliateNumber);
          accountSfId && accountSfId.length > 0 && sessionStorage.setItem(AFFILIATE_ID_KEY, accountSfId);
          email && sessionStorage.setItem(AFFILIATE_EMAIL_ID, email);

          // Set super user status in Redux state
          dispatch(setSuperUserState(true));
          // Keep sessionStorage as backup for persistence across page refreshes
          sessionStorage.setItem(SUPER_USER_KEY, 'true');

          const bannerStatusLocalObject: any = JSON.parse(localStorage.getItem('bannerStatus') || '{}')

          if (!('ssoBanner' in bannerStatusLocalObject))
            localStorage.setItem('bannerStatus', JSON.stringify({
              ...bannerStatusLocalObject,
              ssoBanner: true
            }));

          dispatch(setUserAuthDetail(response.data));
        }

        dispatch(getAffiliateDetails())
          .then(() => {
            resolve(response);
          })
          .catch((error) => {
            reject(error);
          });
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const clearSession = (navigate: any = null) => (dispatch: Function) =>
  new Promise((resolve) => {
    TokenManager.clearAllTokens();

    cookie.remove(AFFILIATE_NUMBER_KEY, { path: COOKIE_PATH });
    cookie.remove(AFFILIATE_ID_KEY, { path: COOKIE_PATH });
    cookie.remove('Login.Successful', { path: COOKIE_PATH });
    cookie.remove(SSO_CDH_COMPANY_KEY, { path: COOKIE_PATH });

    sessionStorage.removeItem(AFFILIATE_NUMBER_KEY);
    sessionStorage.removeItem(AFFILIATE_ID_KEY);
    sessionStorage.removeItem(AUTHENTICATED_BY_OCTA);
    sessionStorage.removeItem(SUPER_USER_KEY);
    sessionStorage.setItem(LOGGED_OUT, "true");

    navigate ? navigate("/login") : window.location.href = '/login';

    dispatch(setUserAuthState(false));
    dispatch(setInitialState());
    dispatch(reSetUserAuth());
    persistLogout();
    resolve(true);
  });

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const refreshToken = async (dispatch: any) => {
  if (isRefreshing) {
    return new Promise<string>((resolve) => {
      refreshSubscribers.push(resolve);
    });
  }

  isRefreshing = true;

  const referringCompany: string = cookieHelper.parse(document.cookie).cdhCompany;

  // Check authenticated_by_octa dynamically to set the correct scope
  const isAuthByOcta = Boolean(sessionStorage.getItem(AUTHENTICATED_BY_OCTA));
  const refreshTokenScope: string = isAuthByOcta ? 'oktaAuth' : 'affiliate_portal';

  try {
    const response = await dispatch(
      authenticate(
        {
          refresh_token: TokenManager.getRefreshToken(),
          orgCode: getOrganizationCode(referringCompany),
        },
        { scope: refreshTokenScope },
        REFRESH_URL,
        true
      )
    );

    // Notify all waiting requests
    refreshSubscribers.forEach((callback) => callback(response.data));
    refreshSubscribers = [];

    return response.data;
  } catch (error) {
    console.error("Refresh token failed", error);
    throw error;
  } finally {
    isRefreshing = false;
  }
};

const retryRequests = (dispatch: any, resolve: any, reject: any) => {
  dispatch(getAffiliateDetails())
    .then(resolve)
    .catch((error) => {
      dispatch(getServicesErrorObj(error));
      reject(error);
    });
};

export const refresh = (location: any = null) => (dispatch: Function) => new Promise((resolve, reject) => {
  sessionStorage.removeItem('logged-out');

  const currentRoute = window.location.pathname;
  if (currentRoute === '/maintenance') {
    resolve(true);
  } else {
    if (TokenManager.getRefreshToken()) {
      if (isRefreshing) {
        resolve(true);
      }
      else {
        refreshToken(dispatch)
          .then(() => retryRequests(dispatch, resolve, reject))
          .catch((error: any) => {
            dispatch(clearSession());
            reject(error);
          });
      }
    } else {
      dispatch(clearSession());
      reject();
    }
  }
});

export const request = (config: any, isRetry: boolean = false) => (dispatch: Function) =>
  new Promise((resolve, reject) => {
    const handleRetry: Function = () => {
      dispatch(refresh())
        .then(() => {
          dispatch(request(config, true))
            .then(resolve)
            .catch(reject);
        })
        .catch((error: any) => {
          reject(error);
        });
    };

    const authorizationHeaderValue = config?.headers?.Authorization ?? (TokenManager.getAuthorizationHeader());

    const affiliateNumber: string | null = (sessionStorage.getItem('affiliate-number'));

    axios({
      ...config,
      headers: {
        ...config.headers,
        "Access-Control-Allow-Origin": "*",
        Authorization: authorizationHeaderValue,
        "Content-Type": "application/json",
        "affiliate_number": affiliateNumber
      },
    })
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: any) => {
        if (axios.isCancel(error)) {
          console.log("axios request cancelled", error.message, ...config.url);
        } else {
          if (!isRetry && error.request && error.response?.status === 401) {
            handleRetry();
          } else {
            error && reject(error);
          }
        }
      });
  });

export const get = (url: string) => (dispatch: Function) =>
  dispatch(
    request({
      url,
      method: "GET"
    })
  );

export const post = (url: string, data: any) => (dispatch: Function) =>
  dispatch(
    request({
      url,
      method: "POST",
      data
    })
  );

export const put = (url: string, data: any) => (dispatch: Function) =>
  dispatch(
    request({
      url,
      method: 'PUT',
      data
    })
  );

export const affiliate = () => (cookie.get(AFFILIATE_NUMBER_KEY) || '');

export const setInitialState = () => {
  return { type: RESET_REDUX_STORE_TO_INITIAL_STATE };
}